'use client';

import { useEffect } from 'react';

import { SWEETLINK_AUTO_KEY, SWEETLINK_AUTO_RESUME_FROM_KEY } from '@/lib/sweetlink/constants';
import { getBrowserWindow } from '@/lib/utils/browser-window';

type SweetLinkRuntimeWindow =
  | null
  | (NonNullable<ReturnType<typeof getBrowserWindow>> & {
      __sweetlinkResumeRedirectActive?: boolean;
      __sweetlinkCliAuto?: boolean;
      __sweetlinkCliAutoInFlight?: boolean;
    });

export function SweetLinkResumeRedirect(): null {
  useEffect(() => {
    const runtimeWindow: SweetLinkRuntimeWindow = getBrowserWindow();
    if (!runtimeWindow) {
      return;
    }
    runtimeWindow.__sweetlinkResumeRedirectActive = true;

    const maybeRedirect = () => {
      try {
        const resumeTarget = runtimeWindow.sessionStorage?.getItem(SWEETLINK_AUTO_RESUME_FROM_KEY);
        if (resumeTarget && resumeTarget !== runtimeWindow.location.href) {
          if (typeof console?.info === 'function') {
            console.info('[SweetLink CLI] resume redirect', {
              resumeTarget,
              href: runtimeWindow.location.href,
            });
          }
          runtimeWindow.__sweetlinkCliAuto = true;
          try {
            runtimeWindow.sessionStorage?.setItem(SWEETLINK_AUTO_KEY, 'pending');
          } catch {
            /* ignore sessionStorage failures */
          }
          try {
            runtimeWindow.dispatchEvent(new CustomEvent('sweetlink:cli-auto'));
          } catch {
            /* ignore dispatch failures */
          }
          runtimeWindow.location.replace(resumeTarget);
          return true;
        }
      } catch {
        /* ignore sessionStorage failures */
      }
      return false;
    };

    if (maybeRedirect()) {
      return;
    }

    const intervalId = runtimeWindow.setInterval(() => {
      if (maybeRedirect()) {
        runtimeWindow.clearInterval(intervalId);
      }
    }, 250);

    return () => {
      runtimeWindow.clearInterval(intervalId);
    };
  }, []);

  return null;
}
